import React, {ReactElement, useState, useMemo, useEffect} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import DesignCard from './DesignCard';
import services from '../../api/remoteServices';
import {usePrevious} from '../../hooks';
import {useFetchDesignsQuery} from '../../api/designsApi';
import './index.scss';

type UserDesignsProps = {
  email: string;
  setDesign: (selectedDesignCid: string) => void;
  selectedDesign: string | undefined;
};

const UserDesigns: React.FC<UserDesignsProps> = ({
  email,
  setDesign,
  selectedDesign
}: UserDesignsProps): ReactElement => {
  const [page, setPage] = useState(1);
  const previousEmail = usePrevious(email);
  const {
    data: designResults,
    isSuccess,
    isError,
    isFetching,
    isLoading
  } = useFetchDesignsQuery({
    email,
    page
  });

  useEffect(() => {
    if (email && previousEmail && previousEmail !== email) {
      setPage(1);
    }
  }, [previousEmail, email]);

  const navigationContainer = useMemo(() => {
    const handlePageChange = (event: React.ChangeEvent, value: number) => {
      setPage(value);
    };

    return (
      <div>
        {(isSuccess || isError) &&
          designResults?.designs &&
          designResults?.total_count > 8 && (
            <Pagination
              count={designResults.total_pages}
              defaultPage={page}
              onChange={handlePageChange}
              size="large"
            />
          )}
      </div>
    );
  }, [page, setPage, designResults]);

  const shouldShowSpinner = isLoading || isFetching;

  return (
    <div className="userDesigns">
      <h1>Start with a design you&rsquo;ve already saved.</h1>
      <p>
        Pick from your saved designs. If you want to use a new design,
        <a href={`${services.homePage}/ndx`} target="_blank" rel="noreferrer">
          &nbsp;create it in the Design Lab&nbsp;
        </a>
        and save it to your account.
      </p>
      {shouldShowSpinner && <CircularProgress className="loadingSpinner" />}
      {!shouldShowSpinner && !!designResults?.designs.length && (
        <div className="designContainer">
          {designResults.designs.map(design => (
            <DesignCard
              selected={design.composite_id === selectedDesign}
              setDesign={setDesign}
              design={design}
              key={design.composite_id}
            />
          ))}
        </div>
      )}
      {navigationContainer}
    </div>
  );
};

export default UserDesigns;
